import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import api, { haccpSchedeApi, aziendeApi } from '../lib/api';
import { toast } from 'sonner';
import { Sparkles, Upload, FileText, Pencil, Trash2, FileCode, Save, Loader2, Building2, Scan, Download, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../components/ui/select';
import AuthImg from '../components/AuthImg';

const emptyProd = {
  nome_prodotto: '', denominazione_legale: 'PRODOTTO DOLCIARIO', descrizione: '',
  ingredienti: '', allergeni: '',
  valori_nutrizionali: { energia_kcal: null, energia_kJ: null, grassi: null, grassi_saturi: null, carboidrati: null, zuccheri: null, fibre: null, proteine: null, sale: null },
  peso_netto: '', peso_sgocciolato: '', lotto: '', tmc: '', ean: '',
  produttore_nome: '', produttore_indirizzo: '',
  imballaggio: '',
  imballaggio_dettagliato: {},
  shelf_life: '', condizioni_conservazione: '', note: '',
};

const IMBALLAGGIO_FIELDS = [
  { key: 'involucro_primario', label: 'Involucro primario', placeholder: 'es. Film polipropilene metallizzato 30µ' },
  { key: 'etichetta', label: 'Etichetta', placeholder: 'es. Carta autoadesiva stampata fronte' },
  { key: 'nastro', label: 'Nastro', placeholder: 'es. Nastro rasatello satin 15mm' },
  { key: 'laccetto', label: 'Laccetto', placeholder: 'es. Cordoncino cotone naturale' },
  { key: 'sacchetto', label: 'Sacchetto', placeholder: 'es. Sacchetto PE trasparente' },
  { key: 'pirottino', label: 'Pirottino', placeholder: 'es. Pirottino carta paraffinata oro' },
  { key: 'vassoio', label: 'Vassoio', placeholder: 'es. Vassoio cartone alimentare' },
  { key: 'astuccio', label: 'Astuccio', placeholder: 'es. Astuccio cartoncino 300g/m²' },
  { key: 'confezione_secondaria', label: 'Confezione secondaria', placeholder: 'es. Scatola regalo' },
  { key: 'pallet', label: 'Pallet / Imballo terziario', placeholder: 'es. Cartone americano 6 pz' },
];

const emptyAzienda = {
  ragione_sociale: '', slogan: 'Prodotto Artigianale',
  indirizzo: '', cap: '', comune: '', provincia: '',
  partita_iva: '', telefono: '', email: '',
  revisione_documento: 'REV. 0',
};

export default function HaccpSchedeTecnichePage() {
  const [prodotti, setProdotti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [extracting, setExtracting] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openAzienda, setOpenAzienda] = useState(false);
  const [openReview, setOpenReview] = useState(false);
  const [form, setForm] = useState(emptyProd);
  const [azienda, setAzienda] = useState(emptyAzienda);
  const [extractedItems, setExtractedItems] = useState([]);
  const [aziendeDisp, setAziendeDisp] = useState([]);
  const [aiSensLoading, setAiSensLoading] = useState(false);
  const [bulkSelected, setBulkSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const toggleBulk = (id) => {
    const next = new Set(bulkSelected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setBulkSelected(next);
  };
  const selectAll = () => setBulkSelected(new Set(prodotti.map(p => p.id)));
  const selectNone = () => setBulkSelected(new Set());

  const downloadBulk = async (ids) => {
    if (!ids.length) { toast.error('Nessun prodotto selezionato'); return; }
    setBulkBusy(true);
    try {
      toast.info(`Generazione di ${ids.length} schede in corso…`);
      const r = await api.post('/haccp/schede/prodotti/genera-bulk', { prodotto_ids: ids }, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Schede_Tecniche_Bulk_${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`✅ ZIP con ${ids.length} schede scaricato`);
      setBulkSelected(new Set());
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore generazione bulk');
    } finally { setBulkBusy(false); }
  };
  // v63: aggiunta tipo imballaggio personalizzato
  const [imbCustomMode, setImbCustomMode] = useState(false);
  const [imbCustomLabel, setImbCustomLabel] = useState('');

  // Sanitizza la label utente in una chiave dict ([a-z0-9_])
  const toImbKey = (label) => {
    const base = (label || '')
      .toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
    return base || `custom_${Date.now()}`;
  };
  // Formatta una chiave (predefinita o custom) in label leggibile per i custom
  const prettyImbLabel = (key) => (key || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  const addCustomImb = () => {
    const lbl = (imbCustomLabel || '').trim();
    if (!lbl) { toast.error('Inserisci un nome per il tipo personalizzato'); return; }
    let key = toImbKey(lbl);
    const existing = form.imballaggio_dettagliato || {};
    // se la chiave esiste già, aggiungi un suffisso numerico
    let unique = key;
    let i = 2;
    while (Object.prototype.hasOwnProperty.call(existing, unique)) {
      unique = `${key}_${i++}`;
    }
    setForm({ ...form, imballaggio_dettagliato: { ...existing, [unique]: '' } });
    setImbCustomLabel('');
    setImbCustomMode(false);
  };

  const load = async () => {
    try {
      setLoading(true);
      const [pr, az, azGest] = await Promise.all([
        haccpSchedeApi.getProdotti(),
        haccpSchedeApi.getAzienda(),
        aziendeApi.list().catch(() => ({ data: [] })),
      ]);
      setProdotti(pr.data || []);
      if (az.data) setAzienda({ ...emptyAzienda, ...az.data });
      setAziendeDisp(azGest.data || []);
    } catch {
      toast.error('Errore caricamento');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const syncFromAzienda = async (aziendaId) => {
    if (!aziendaId) return;
    try {
      const r = await haccpSchedeApi.syncAziendaFrom(aziendaId);
      setAzienda({ ...emptyAzienda, ...r.data });
      toast.success('Dati importati da Gestione Aziendale');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore import');
    }
  };

  const handleExtract = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!azienda.ragione_sociale) {
      toast.error('Configura prima i dati azienda HACCP'); setOpenAzienda(true); return;
    }
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await haccpSchedeApi.extractLabels(fd);
      const items = (r.data?.prodotti || []).map(p => ({ ...emptyProd, ...p, valori_nutrizionali: { ...emptyProd.valori_nutrizionali, ...(p.valori_nutrizionali || {}) } }));
      setExtractedItems(items);
      setOpenReview(true);
      toast.success(`Rilevati ${items.length} prodotti — controlla e conferma`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore estrazione AI');
    } finally {
      setExtracting(false);
      e.target.value = '';
    }
  };

  const confirmBulkSave = async () => {
    try {
      const r = await haccpSchedeApi.bulkCreateProdotti(extractedItems);
      toast.success(`Salvati ${r.data.inserted} prodotti`);
      setOpenReview(false);
      setExtractedItems([]);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    }
  };

  const openNew = () => { setForm(emptyProd); setOpenEdit(true); };
  const openEditing = (p) => { setForm({ ...emptyProd, ...p, valori_nutrizionali: { ...emptyProd.valori_nutrizionali, ...(p.valori_nutrizionali || {}) }, imballaggio_dettagliato: { ...emptyProd.imballaggio_dettagliato, ...(p.imballaggio_dettagliato || {}) } }); setOpenEdit(true); };

  const saveProd = async () => {
    if (!form.nome_prodotto) { toast.error('Nome prodotto obbligatorio'); return; }
    try {
      if (form.id) await haccpSchedeApi.updateProdotto(form.id, form);
      else await haccpSchedeApi.createProdotto(form);
      toast.success('Prodotto salvato');
      setOpenEdit(false); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };

  const del = async (id) => {
    if (!window.confirm('Cancellare questo prodotto?')) return;
    try { await haccpSchedeApi.deleteProdotto(id); load(); toast.success('Cancellato'); }
    catch { toast.error('Errore'); }
  };

  const generate = async (p) => {
    if (!azienda.ragione_sociale) { toast.error('Configura prima i dati azienda'); setOpenAzienda(true); return; }
    try {
      const r = await haccpSchedeApi.generateScheda(p.id);
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Scheda_Tecnica_${(p.nome_prodotto || 'Prodotto').replace(/[^a-zA-Z0-9]/g, '_')}.docx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Scheda Tecnica generata');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore generazione');
    }
  };

  const saveAzienda = async () => {
    if (!azienda.ragione_sociale) { toast.error('Ragione sociale obbligatoria'); return; }
    try {
      await haccpSchedeApi.saveAzienda(azienda);
      toast.success('Dati azienda salvati');
      setOpenAzienda(false);
    } catch (e) { toast.error('Errore salvataggio azienda'); }
  };

  const updateExtracted = (idx, field, val) => {
    setExtractedItems(items => items.map((it, i) => i === idx ? { ...it, [field]: val } : it));
  };
  const updateExtractedNutr = (idx, field, val) => {
    setExtractedItems(items => items.map((it, i) => i === idx ? { ...it, valori_nutrizionali: { ...it.valori_nutrizionali, [field]: val === '' ? null : parseFloat(val) } } : it));
  };

  return (
    <div className="space-y-6" data-testid="haccp-schede-page">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Sparkles className="w-7 h-7 text-orange-600" /> Schede Tecniche Prodotti
          </h1>
          <p className="text-sm text-slate-600 mt-1">Carica foto/PDF di etichette → l&#39;AI estrae i dati → genera la Scheda Tecnica .docx con un click</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setOpenAzienda(true)} data-testid="azienda-config-btn">
            <Building2 className="w-4 h-4 mr-1" /> {azienda.ragione_sociale ? 'Modifica azienda' : 'Configura azienda'}
          </Button>
          <Button variant="outline" onClick={openNew} data-testid="manual-add-btn">
            <Pencil className="w-4 h-4 mr-1" /> Aggiungi manualmente
          </Button>
          <label className="inline-flex">
            <input type="file" accept=".zip" className="hidden" data-testid="bulk-zip-input"
              onChange={async (e) => {
                const f = e.target.files?.[0]; if (!f) return;
                try {
                  const fd = new FormData(); fd.append('file', f);
                  const r = await haccpSchedeApi.bulkUploadZip(fd);
                  toast.success(`${r.data.matched}/${r.data.folders_total} cartelle abbinate · ${r.data.photos_added} foto aggiunte`);
                  load();
                } catch (err) { toast.error(err.response?.data?.detail || 'Errore bulk-zip'); }
                e.target.value = '';
              }} />
            <Button asChild variant="outline" data-testid="bulk-zip-btn"><span><Upload className="w-4 h-4 mr-1" />Foto zip</span></Button>
          </label>
          <label className="inline-flex">
            <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={handleExtract} data-testid="upload-labels-input" />
            <Button asChild data-testid="upload-labels-btn" disabled={extracting}>
              <span>
                {extracting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Scan className="w-4 h-4 mr-1" />}
                {extracting ? 'AI in corso...' : 'Carica etichette (AI)'}
              </span>
            </Button>
          </label>
        </div>
      </motion.div>

      {!azienda.ragione_sociale && (
        <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded">
          <p className="text-sm text-amber-800"><strong>Prima cosa:</strong> configura i dati della tua azienda HACCP cliccando &quot;Configura azienda&quot; — appariranno in alto sulla Scheda Tecnica.</p>
        </div>
      )}

      {loading ? <p>Caricamento...</p> : prodotti.length === 0 ? (
        <div className="bg-white p-10 rounded-lg text-center border" data-testid="empty-state">
          <FileText className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-600">Nessun prodotto. Carica un PDF/immagine di etichette per iniziare.</p>
        </div>
      ) : (
        <>
          {/* v71b: Bulk-select toolbar */}
          <div className="bg-amber-50 border border-amber-200 rounded p-2 mb-3 flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">{bulkSelected.size}/{prodotti.length} selezionati</span>
            <Button size="sm" variant="outline" onClick={selectAll} data-testid="select-all-btn">Seleziona tutti</Button>
            <Button size="sm" variant="outline" onClick={selectNone}>Deseleziona</Button>
            <div className="flex-1" />
            <Button size="sm" onClick={() => downloadBulk(Array.from(bulkSelected))}
              disabled={bulkBusy || bulkSelected.size === 0}
              className="bg-emerald-600 hover:bg-emerald-700" data-testid="bulk-selected-btn">
              {bulkBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
              Genera selezionate ({bulkSelected.size})
            </Button>
            <Button size="sm" onClick={() => downloadBulk(prodotti.map(p => p.id))} disabled={bulkBusy} variant="default" data-testid="bulk-all-btn">
              {bulkBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
              Genera TUTTE
            </Button>
          </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {prodotti.map(p => (
            <motion.div key={p.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={`bg-white p-4 rounded-lg shadow-sm border flex flex-col ${bulkSelected.has(p.id) ? 'ring-2 ring-emerald-500' : ''}`} data-testid={`prodotto-card-${p.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-start gap-2 flex-1 min-w-0">
                  <input type="checkbox" checked={bulkSelected.has(p.id)} onChange={() => toggleBulk(p.id)}
                    className="mt-1 h-4 w-4" data-testid={`bulk-${p.id}`} onClick={e => e.stopPropagation()} />
                  <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-800 truncate">{p.nome_prodotto}</h3>
                  {p.peso_netto && <p className="text-xs text-slate-500">{p.peso_netto}</p>}
                  {p.created_by_name && (
                    <p className="text-[10px] text-slate-400 mt-0.5">creato da: {p.created_by_name}</p>
                  )}
                  </div>
                </div>
                {p.ean && <Badge variant="outline" className="text-xs">EAN: {p.ean.slice(-4)}</Badge>}
              </div>
              {p.valori_nutrizionali?.energia_kcal && (
                <p className="text-xs text-slate-600 mb-1">{p.valori_nutrizionali.energia_kcal} kcal / 100g</p>
              )}
              {p.allergeni && <p className="text-xs italic text-orange-700 line-clamp-2 mb-2">⚠ {p.allergeni}</p>}
              {/* v58: Gallery foto — caricamento aperto a tutti gli utenti HACCP (collaborazione) */}
              {p.foto_paths && p.foto_paths.length > 0 ? (
                <div className="flex items-center gap-1 mb-2 overflow-x-auto">
                  {p.foto_paths.slice(0, 4).map((_, idx) => (
                    <AuthImg key={idx} url={haccpSchedeApi.getFotoUrl(p.id, idx)}
                      className="w-12 h-12 object-cover rounded border bg-slate-50" alt={`foto ${idx + 1}`} />
                  ))}
                  {p.foto_paths.length > 4 && (
                    <div className="w-12 h-12 flex items-center justify-center rounded border bg-slate-100 text-xs text-slate-500">+{p.foto_paths.length - 4}</div>
                  )}
                  <label className="ml-1">
                    <input type="file" multiple accept=".png,.jpg,.jpeg,.webp" className="hidden"
                      onChange={async (e) => {
                        const files = Array.from(e.target.files || []);
                        if (!files.length) return;
                        try {
                          const fd = new FormData();
                          files.forEach(f => fd.append('files', f));
                          await haccpSchedeApi.uploadFoto(p.id, fd);
                          toast.success(`${files.length} foto aggiunte`);
                          load();
                        } catch (err) { toast.error(err.response?.data?.detail || 'Errore upload'); }
                        e.target.value = '';
                      }} />
                    <span className="cursor-pointer inline-flex items-center justify-center w-12 h-12 rounded border border-dashed bg-white hover:bg-slate-50" title="Aggiungi foto">
                      <Upload className="w-4 h-4 text-slate-500" />
                    </span>
                  </label>
                </div>
              ) : (
                <label className="inline-block mb-2">
                  <input type="file" multiple accept=".png,.jpg,.jpeg,.webp" className="hidden"
                    onChange={async (e) => {
                      const files = Array.from(e.target.files || []);
                      if (!files.length) return;
                      try {
                        const fd = new FormData();
                        files.forEach(f => fd.append('files', f));
                        await haccpSchedeApi.uploadFoto(p.id, fd);
                        toast.success(`${files.length} foto caricate`);
                        load();
                      } catch (err) { toast.error(err.response?.data?.detail || 'Errore upload foto'); }
                      e.target.value = '';
                    }} />
                  <Button asChild size="sm" variant="outline" className="text-xs"><span><Upload className="w-3 h-3 mr-1" /> Aggiungi foto</span></Button>
                </label>
              )}
              <div className="flex gap-1 mt-auto pt-2 border-t">
                <Button size="sm" onClick={() => generate(p)} data-testid={`generate-${p.id}`}>
                  <FileCode className="w-3.5 h-3.5 mr-1" /> Genera
                </Button>
                {p.can_edit && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => openEditing(p)} data-testid={`edit-${p.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => del(p.id)} className="text-red-600 ml-auto" data-testid={`delete-${p.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </>
                )}
              </div>
            </motion.div>
          ))}
        </div>
        </>
      )}

      {/* Dialog Azienda */}
      <Dialog open={openAzienda} onOpenChange={setOpenAzienda}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Configura azienda HACCP</DialogTitle><DialogDescription>I dati appariranno in intestazione e footer della Scheda Tecnica.</DialogDescription></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {aziendeDisp.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded p-3">
                <Label className="text-xs font-semibold text-blue-900">⚡ Importa da Gestione Aziendale</Label>
                <div className="flex gap-2 mt-2">
                  <select className="flex-1 border rounded px-2 py-1 text-sm" data-testid="azienda-sync-select"
                    onChange={(e) => { if (e.target.value) syncFromAzienda(e.target.value); }} defaultValue="">
                    <option value="">— Seleziona azienda —</option>
                    {aziendeDisp.map(a => <option key={a.id} value={a.id}>{a.denominazione}</option>)}
                  </select>
                </div>
                <p className="text-xs text-blue-700 mt-1">Auto-compila ragione sociale, P.IVA, indirizzo, contatti dai dati salvati in Gestione Aziendale.</p>
              </div>
            )}
            <div><Label>Ragione sociale *</Label><Input value={azienda.ragione_sociale} onChange={e => setAzienda({ ...azienda, ragione_sociale: e.target.value })} data-testid="az-rs" /></div>
            <div><Label>Slogan</Label><Input value={azienda.slogan} onChange={e => setAzienda({ ...azienda, slogan: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Indirizzo</Label><Input value={azienda.indirizzo} onChange={e => setAzienda({ ...azienda, indirizzo: e.target.value })} /></div>
              <div><Label>CAP</Label><Input value={azienda.cap} onChange={e => setAzienda({ ...azienda, cap: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Comune</Label><Input value={azienda.comune} onChange={e => setAzienda({ ...azienda, comune: e.target.value })} /></div>
              <div><Label>Provincia</Label><Input value={azienda.provincia} onChange={e => setAzienda({ ...azienda, provincia: e.target.value })} maxLength={2} /></div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label>P.IVA</Label><Input value={azienda.partita_iva} onChange={e => setAzienda({ ...azienda, partita_iva: e.target.value })} /></div>
              <div><Label>Telefono</Label><Input value={azienda.telefono} onChange={e => setAzienda({ ...azienda, telefono: e.target.value })} /></div>
              <div><Label>Email</Label><Input value={azienda.email} onChange={e => setAzienda({ ...azienda, email: e.target.value })} /></div>
            </div>
            <div><Label>Revisione documento</Label><Input value={azienda.revisione_documento} onChange={e => setAzienda({ ...azienda, revisione_documento: e.target.value })} placeholder="REV. 0 02/2026" /></div>
            <div className="border-t pt-3">
              <Label>Logo aziendale (PNG/JPG, max 2 MB)</Label>
              <div className="flex items-center gap-2 mt-1">
                <input type="file" accept=".png,.jpg,.jpeg,.webp" onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  try {
                    const fd = new FormData();
                    fd.append('file', file);
                    await haccpSchedeApi.uploadLogo(fd);
                    toast.success('Logo caricato');
                    setAzienda(a => ({ ...a, has_logo: true }));
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Errore upload logo');
                  }
                  e.target.value = '';
                }} data-testid="upload-logo-input" />
                {azienda.logo_path && (
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={async () => {
                    if (!window.confirm('Rimuovere il logo?')) return;
                    try {
                      await haccpSchedeApi.deleteLogo();
                      setAzienda(a => ({ ...a, logo_path: null }));
                      toast.success('Logo rimosso');
                    } catch { toast.error('Errore'); }
                  }}><Trash2 className="w-4 h-4" /></Button>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-1">Apparirà in alto a sinistra di ogni Scheda Tecnica.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenAzienda(false)}>Annulla</Button>
            <Button onClick={saveAzienda} data-testid="save-azienda-btn"><Save className="w-4 h-4 mr-1" /> Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Edit prodotto */}
      <Dialog open={openEdit} onOpenChange={setOpenEdit}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>{form.id ? 'Modifica' : 'Nuovo'} prodotto</DialogTitle></DialogHeader>
          <Tabs defaultValue="base" className="max-h-[65vh] overflow-y-auto">
            <TabsList>
              <TabsTrigger value="base">Base</TabsTrigger>
              <TabsTrigger value="nutr">Nutrizionale</TabsTrigger>
              <TabsTrigger value="imb">Imballaggio</TabsTrigger>
              <TabsTrigger value="extra">Caratteristiche</TabsTrigger>
              <TabsTrigger value="foto">Foto</TabsTrigger>
            </TabsList>
            <TabsContent value="base" className="space-y-3 mt-3">
              <div><Label>Nome prodotto *</Label><Input value={form.nome_prodotto} onChange={e => setForm({ ...form, nome_prodotto: e.target.value })} data-testid="form-nome" /></div>
              <div><Label>Denominazione legale</Label><Input value={form.denominazione_legale} onChange={e => setForm({ ...form, denominazione_legale: e.target.value })} /></div>
              <div><Label>Descrizione</Label><Input value={form.descrizione} onChange={e => setForm({ ...form, descrizione: e.target.value })} /></div>
              <div><Label>Ingredienti</Label><Textarea value={form.ingredienti} onChange={e => setForm({ ...form, ingredienti: e.target.value })} rows={3} /></div>
              <div><Label>Allergeni</Label><Textarea value={form.allergeni} onChange={e => setForm({ ...form, allergeni: e.target.value })} rows={2} /></div>
              <div className="grid grid-cols-3 gap-2">
                <div><Label>Peso netto</Label><Input value={form.peso_netto} onChange={e => setForm({ ...form, peso_netto: e.target.value })} /></div>
                <div><Label>Lotto</Label><Input value={form.lotto} onChange={e => setForm({ ...form, lotto: e.target.value })} /></div>
                <div><Label>TMC</Label><Input value={form.tmc} onChange={e => setForm({ ...form, tmc: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>EAN</Label><Input value={form.ean} onChange={e => setForm({ ...form, ean: e.target.value })} /></div>
                <div><Label>Imballaggio</Label><Input value={form.imballaggio} onChange={e => setForm({ ...form, imballaggio: e.target.value })} /></div>
              </div>
              <div><Label>Produttore (nome)</Label><Input value={form.produttore_nome} onChange={e => setForm({ ...form, produttore_nome: e.target.value })} /></div>
              <div><Label>Produttore (indirizzo)</Label><Input value={form.produttore_indirizzo} onChange={e => setForm({ ...form, produttore_indirizzo: e.target.value })} /></div>
            </TabsContent>
            <TabsContent value="nutr" className="space-y-2 mt-3">
              {['energia_kcal', 'energia_kJ', 'grassi', 'grassi_saturi', 'carboidrati', 'zuccheri', 'fibre', 'proteine', 'sale'].map(k => (
                <div key={k} className="grid grid-cols-2 gap-2 items-center">
                  <Label className="text-sm">{k.replace('_', ' ')}</Label>
                  <Input type="number" step="0.01" value={form.valori_nutrizionali?.[k] ?? ''} onChange={e => setForm({ ...form, valori_nutrizionali: { ...form.valori_nutrizionali, [k]: e.target.value === '' ? null : parseFloat(e.target.value) } })} />
                </div>
              ))}
            </TabsContent>
            <TabsContent value="imb" className="space-y-3 mt-3">
              <p className="text-sm text-slate-600 bg-amber-50 border-l-4 border-amber-400 p-3 rounded">
                Aggiungi solo i materiali presenti sul tuo prodotto. I tipi non aggiunti non verranno mostrati nella Scheda Tecnica.
              </p>

              {/* v65: Copia imballaggio da un altro prodotto */}
              {(() => {
                const sourceProducts = (prodotti || []).filter(p =>
                  p.id !== form.id && p.imballaggio_dettagliato && Object.keys(p.imballaggio_dettagliato).length > 0
                );
                if (sourceProducts.length === 0) return null;
                return (
                  <div className="flex gap-2 items-end bg-emerald-50 border border-emerald-200 p-2 rounded">
                    <div className="flex-1">
                      <Label className="text-xs text-emerald-900">📋 Copia imballaggio da un altro prodotto</Label>
                      <Select value="" onValueChange={(srcId) => {
                        if (!srcId) return;
                        const src = sourceProducts.find(p => p.id === srcId);
                        if (!src) return;
                        const existing = form.imballaggio_dettagliato || {};
                        if (Object.keys(existing).length > 0 &&
                            !window.confirm(`Sostituire le ${Object.keys(existing).length} voci di imballaggio attuali con quelle di "${src.nome_prodotto}"?`)) {
                          return;
                        }
                        setForm({ ...form, imballaggio_dettagliato: { ...(src.imballaggio_dettagliato || {}) } });
                        toast.success(`Imballaggio copiato da "${src.nome_prodotto}"`);
                      }}>
                        <SelectTrigger data-testid="imb-copy-from-select" className="bg-white">
                          <SelectValue placeholder={`Seleziona prodotto sorgente (${sourceProducts.length} disponibili)…`} />
                        </SelectTrigger>
                        <SelectContent>
                          {sourceProducts.map(p => {
                            const nVoci = Object.keys(p.imballaggio_dettagliato || {}).length;
                            return (
                              <SelectItem key={p.id} value={p.id}>
                                {p.nome_prodotto} <span className="text-xs text-slate-500">({nVoci} {nVoci === 1 ? 'voce' : 'voci'})</span>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                );
              })()}

              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Label className="text-xs">Tipo materiale</Label>
                  <Select value="" onValueChange={(val) => {
                    if (!val) return;
                    if (val === '__custom__') {
                      setImbCustomMode(true);
                      return;
                    }
                    if (!(form.imballaggio_dettagliato || {})[val]) {
                      setForm({ ...form, imballaggio_dettagliato: { ...form.imballaggio_dettagliato, [val]: '' } });
                    }
                  }}>
                    <SelectTrigger data-testid="imb-add-select"><SelectValue placeholder="Seleziona per aggiungere..." /></SelectTrigger>
                    <SelectContent>
                      {IMBALLAGGIO_FIELDS.filter(f => !(form.imballaggio_dettagliato || {})[f.key]).map(f => (
                        <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>
                      ))}
                      <SelectItem value="__custom__">+ Aggiungi tipo personalizzato…</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {imbCustomMode && (
                <div className="flex gap-2 items-end bg-blue-50 border border-blue-200 p-2 rounded">
                  <div className="flex-1">
                    <Label className="text-xs">Nome tipo personalizzato</Label>
                    <Input
                      value={imbCustomLabel}
                      placeholder="es. Pellicola alimentare, Fascetta, Box termico…"
                      autoFocus
                      onChange={e => setImbCustomLabel(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomImb(); } }}
                      data-testid="imb-custom-input"
                    />
                  </div>
                  <Button size="sm" onClick={addCustomImb} data-testid="imb-custom-add-btn">Aggiungi</Button>
                  <Button size="sm" variant="ghost" onClick={() => { setImbCustomMode(false); setImbCustomLabel(''); }}>Annulla</Button>
                </div>
              )}

              <div className="space-y-2">
                {Object.entries(form.imballaggio_dettagliato || {})
                  .filter(([k, v]) => v !== undefined && v !== null)
                  .map(([key, val]) => {
                    const meta = IMBALLAGGIO_FIELDS.find(f => f.key === key) || { label: prettyImbLabel(key), placeholder: '' };
                    return (
                      <div key={key} className="flex gap-2 items-end bg-slate-50 p-2 rounded border">
                        <div className="flex-1">
                          <Label className="text-xs">{meta.label}</Label>
                          <Input
                            value={val || ''}
                            placeholder={meta.placeholder}
                            onChange={e => setForm({ ...form, imballaggio_dettagliato: { ...form.imballaggio_dettagliato, [key]: e.target.value } })}
                            data-testid={`imb-${key}`}
                          />
                        </div>
                        <Button variant="ghost" size="sm" className="text-red-600" onClick={() => {
                          const next = { ...form.imballaggio_dettagliato };
                          delete next[key];
                          setForm({ ...form, imballaggio_dettagliato: next });
                        }} data-testid={`imb-remove-${key}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    );
                  })}
                {Object.keys(form.imballaggio_dettagliato || {}).length === 0 && (
                  <p className="text-sm text-slate-400 italic text-center py-4">Nessuna voce aggiunta. Usa il menu in alto per iniziare.</p>
                )}
              </div>
            </TabsContent>
            <TabsContent value="extra" className="space-y-3 mt-3">
              <div><Label>Shelf-life (TMC esteso)</Label><Input value={form.shelf_life} onChange={e => setForm({ ...form, shelf_life: e.target.value })} placeholder="180 giorni dalla data di produzione" /></div>
              <div><Label>Condizioni di conservazione</Label><Textarea value={form.condizioni_conservazione} onChange={e => setForm({ ...form, condizioni_conservazione: e.target.value })} rows={2} /></div>

              {/* v68b: Caratteristiche sensoriali con AI suggest */}
              <div className="border-t pt-3 mt-3">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-sm text-slate-800">Caratteristiche organolettiche</h4>
                  <Button
                    size="sm"
                    onClick={async () => {
                      if (!form.nome_prodotto?.trim()) { toast.error('Inserisci prima il nome del prodotto'); return; }
                      setAiSensLoading(true);
                      try {
                        const r = await haccpSchedeApi.aiSuggestCaratteristiche({
                          nome_prodotto: form.nome_prodotto,
                          ingredienti: form.ingredienti || '',
                          categoria: form.descrizione || '',
                        });
                        setForm({
                          ...form,
                          caratteristiche_sensoriali: {
                            aspetto: r.data.aspetto,
                            colore: r.data.colore,
                            aroma: r.data.aroma,
                            gusto: r.data.gusto,
                          }
                        });
                        toast.success('✨ Descrizioni generate dall\'AI');
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Errore AI');
                      } finally {
                        setAiSensLoading(false);
                      }
                    }}
                    disabled={aiSensLoading}
                    className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
                    data-testid="ai-suggest-sensoriali"
                  >
                    {aiSensLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                    {aiSensLoading ? 'Generazione…' : 'Suggerisci con AI'}
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mb-3">L'AI usa nome prodotto + ingredienti per generare 4 brevi descrizioni tecniche.</p>
                <div className="space-y-2">
                  {['aspetto', 'colore', 'aroma', 'gusto'].map(k => (
                    <div key={k}>
                      <Label className="text-xs uppercase">{k}</Label>
                      <Textarea
                        rows={2}
                        value={form.caratteristiche_sensoriali?.[k] || ''}
                        onChange={e => setForm({
                          ...form,
                          caratteristiche_sensoriali: { ...(form.caratteristiche_sensoriali || {}), [k]: e.target.value }
                        })}
                        placeholder={`Descrivi il ${k} del prodotto…`}
                        data-testid={`sens-${k}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>
            <TabsContent value="foto" className="space-y-3 mt-3">
              {form.id ? (
                <>
                  <div className="bg-blue-50 border border-blue-200 p-3 rounded text-sm">
                    <p className="font-medium text-blue-900">La prima foto verrà inserita in cima alla Scheda Tecnica.</p>
                    <p className="text-xs text-blue-700 mt-1">JPG/PNG/WEBP max 5 MB cad. Le foto vengono ridimensionate a max 1200px.</p>
                  </div>
                  <label className="inline-block">
                    <input type="file" multiple accept=".png,.jpg,.jpeg,.webp" className="hidden" data-testid="upload-foto-edit"
                      onChange={async (e) => {
                        const files = Array.from(e.target.files || []);
                        if (!files.length) return;
                        try {
                          const fd = new FormData();
                          files.forEach(f => fd.append('files', f));
                          await haccpSchedeApi.uploadFoto(form.id, fd);
                          toast.success(`${files.length} foto caricate`);
                          // Ricarica il prodotto
                          const r = await haccpSchedeApi.getProdotto(form.id);
                          setForm({ ...form, foto_paths: r.data.foto_paths || [] });
                        } catch (err) { toast.error('Errore upload'); }
                        e.target.value = '';
                      }} />
                    <Button asChild><span><Upload className="w-4 h-4 mr-1" /> Carica una o più foto</span></Button>
                  </label>
                  {(form.foto_paths || []).length > 0 && (
                    <Button
                      variant="outline"
                      className="ml-2 border-amber-400 text-amber-700 hover:bg-amber-50"
                      data-testid="recrop-foto-btn"
                      onClick={async () => {
                        if (!window.confirm(`Riapplicare il ritaglio fasce nere alle ${form.foto_paths.length} foto già caricate? L'operazione sovrascrive i file in place.`)) return;
                        try {
                          const r = await haccpSchedeApi.recropFoto(form.id);
                          toast.success(`✂️ Re-crop: ${r.data.cropped} ritagliate, ${r.data.skipped} invariate (${r.data.processed} totali)`);
                          // Bust cache for the gallery thumbs by triggering a setForm
                          const fresh = await haccpSchedeApi.getProdotto(form.id);
                          setForm({ ...form, foto_paths: fresh.data.foto_paths || [], _recropTs: Date.now() });
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Errore re-crop');
                        }
                      }}
                    >
                      <RefreshCw className="w-4 h-4 mr-1" /> Re-crop foto esistenti
                    </Button>
                  )}
                  <div className="grid grid-cols-4 gap-2 mt-3">
                    {(form.foto_paths || []).map((_, idx) => (
                      <div key={idx} className="relative group">
                        <AuthImg url={haccpSchedeApi.getFotoUrl(form.id, idx)} cacheBust={form._recropTs} className="w-full h-24 object-cover rounded border" alt={`foto ${idx + 1}`} />
                        <button className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                          onClick={async () => {
                            if (!window.confirm('Elimina foto?')) return;
                            try {
                              await haccpSchedeApi.deleteFoto(form.id, idx);
                              const r = await haccpSchedeApi.getProdotto(form.id);
                              setForm({ ...form, foto_paths: r.data.foto_paths || [] });
                            } catch { toast.error('Errore'); }
                          }}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                        {idx === 0 && <span className="absolute bottom-1 left-1 bg-green-600 text-white text-[10px] px-1 rounded">★ principale</span>}
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-sm text-slate-500 italic">Salva prima il prodotto, poi potrai caricare le foto.</p>
              )}
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenEdit(false)}>Annulla</Button>
            <Button onClick={saveProd} data-testid="save-prod-btn"><Save className="w-4 h-4 mr-1" /> Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Review prodotti estratti */}
      <Dialog open={openReview} onOpenChange={setOpenReview}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Verifica prodotti estratti dall&apos;AI</DialogTitle>
            <DialogDescription>L&#39;AI ha rilevato {extractedItems.length} prodotti. Verifica e correggi prima di salvare.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {extractedItems.map((it, idx) => (
              <div key={idx} className="border rounded p-3 bg-slate-50">
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <div><Label className="text-xs">Nome</Label><Input value={it.nome_prodotto} onChange={e => updateExtracted(idx, 'nome_prodotto', e.target.value)} /></div>
                  <div><Label className="text-xs">Peso netto</Label><Input value={it.peso_netto || ''} onChange={e => updateExtracted(idx, 'peso_netto', e.target.value)} /></div>
                </div>
                <div className="mb-2"><Label className="text-xs">Ingredienti</Label><Textarea value={it.ingredienti || ''} onChange={e => updateExtracted(idx, 'ingredienti', e.target.value)} rows={2} /></div>
                <div className="grid grid-cols-4 gap-2 mb-2">
                  <div><Label className="text-xs">kcal/100g</Label><Input type="number" value={it.valori_nutrizionali?.energia_kcal ?? ''} onChange={e => updateExtractedNutr(idx, 'energia_kcal', e.target.value)} /></div>
                  <div><Label className="text-xs">grassi g</Label><Input type="number" value={it.valori_nutrizionali?.grassi ?? ''} onChange={e => updateExtractedNutr(idx, 'grassi', e.target.value)} /></div>
                  <div><Label className="text-xs">carbo g</Label><Input type="number" value={it.valori_nutrizionali?.carboidrati ?? ''} onChange={e => updateExtractedNutr(idx, 'carboidrati', e.target.value)} /></div>
                  <div><Label className="text-xs">proteine g</Label><Input type="number" value={it.valori_nutrizionali?.proteine ?? ''} onChange={e => updateExtractedNutr(idx, 'proteine', e.target.value)} /></div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div><Label className="text-xs">EAN</Label><Input value={it.ean || ''} onChange={e => updateExtracted(idx, 'ean', e.target.value)} /></div>
                  <div><Label className="text-xs">Lotto</Label><Input value={it.lotto || ''} onChange={e => updateExtracted(idx, 'lotto', e.target.value)} /></div>
                  <div><Label className="text-xs">TMC</Label><Input value={it.tmc || ''} onChange={e => updateExtracted(idx, 'tmc', e.target.value)} /></div>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpenReview(false); setExtractedItems([]); }}>Annulla</Button>
            <Button onClick={confirmBulkSave} data-testid="confirm-bulk-save"><Save className="w-4 h-4 mr-1" /> Salva tutti</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
